import React, { useEffect, useRef, useState } from 'react';
import type { GameView, TableThrowBroadcast, VoiceTauntBroadcast, ThrowableId } from '@big2/shared';
import { playThrowableSound, playVoiceTaunt, VOICE_TAUNT_SCRIPTS } from '../../tableInteractionsSound.js';
import { ProjectileGraphic, ImpactGraphic } from './TableInteractionGraphics.js';
import './tableInteractions.css';

interface TableInteractionOverlayProps {
  tableThrows: TableThrowBroadcast[];
  voiceTaunts: Record<string, VoiceTauntBroadcast>;
  game: GameView | null;
  isMn?: boolean;
  containerRef?: React.RefObject<HTMLElement | null>;
}

interface ActiveFlight {
  id: string;
  seq: number;
  throwableId: ThrowableId;
  fromPlayerName: string;
  toPlayerName: string;
  fromSeat: number;
  toSeat: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  toWidth?: number;
  toHeight?: number;
  daaluuTarget?: boolean;
  startedAt: number;
  hasImpacted: boolean;
}

const THROW_ACTION_VERBS: Record<ThrowableId, { mn: string; en: string }> = {
  tea: { mn: 'халуун цайгаар шалав 🫖', en: 'splashed hot milk tea on' },
  slipper: { mn: 'улавчаар ташив 🩴', en: 'smacked' },
  egg: { mn: 'өндөг нялав 🥚', en: 'splattered an egg on' },
  airag: { mn: 'айргаар мялаав 🍶', en: 'splashed airag on' },
  cash: { mn: 'мөнгөөр савав 💸', en: 'showered cash on' },
  shagai: { mn: 'шагайгаар няслав 🦴', en: 'flicked a shagai at' },
};

/**
 * Resolves exact seat center position using viewport coordinates.
 * Because the overlay is fixed to the viewport, getBoundingClientRect()
 * guarantees 100% precision onto the player's avatar / head.
 */
function getSeatPosition(seat: number, game: GameView | null): { x: number; y: number; width?: number; height?: number; daaluuTarget?: boolean } {
  if (typeof document !== 'undefined') {
    const target =
      document.querySelector(`[data-daaluu-collection-seat="${seat}"] .daaluu-seat-badge`) ||
      document.querySelector(`[data-seat="${seat}"] .seat-disc`) ||
      document.querySelector(`[data-seat="${seat}"] .self-player-avatar`) ||
      document.querySelector(`[data-seat="${seat}"] .player-avatar`) ||
      document.querySelector(`[data-seat="${seat}"] .avatar`) ||
      document.querySelector(`[data-seat="${seat}"] .avatar-wrapper`) ||
      document.querySelector(`[data-seat="${seat}"] .turn-indicator-disc`) ||
      document.querySelector(`[data-seat="${seat}"]`) ||
      document.querySelector(`[data-daaluu-collection-seat="${seat}"]`);

    if (target) {
      const r = target.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        return {
          x: r.left + r.width / 2,
          y: r.top + r.height / 2,
          width: r.width,
          height: r.height,
          daaluuTarget: target.classList.contains('daaluu-seat-badge'),
        };
      }
    }
  }

  // Fallback calculation if DOM element is not mounted yet
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1000;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

  if (game && game.mySeat === seat) {
    return { x: vw * 0.5, y: vh * 0.88 };
  }

  if (game && game.players.length > 0) {
    const total = game.players.length;
    const relOffset = (seat - game.mySeat + total) % total;
    if (total === 2) {
      return { x: vw * 0.5, y: vh * 0.18 };
    }
    if (total === 3) {
      if (relOffset === 1) return { x: vw * 0.18, y: vh * 0.38 };
      if (relOffset === 2) return { x: vw * 0.82, y: vh * 0.38 };
    }
    if (total === 4) {
      if (relOffset === 1) return { x: vw * 0.16, y: vh * 0.44 };
      if (relOffset === 2) return { x: vw * 0.5, y: vh * 0.16 };
      if (relOffset === 3) return { x: vw * 0.84, y: vh * 0.44 };
    }
    if (total === 5) {
      if (relOffset === 1) return { x: vw * 0.15, y: vh * 0.52 };
      if (relOffset === 2) return { x: vw * 0.28, y: vh * 0.16 };
      if (relOffset === 3) return { x: vw * 0.72, y: vh * 0.16 };
      if (relOffset === 4) return { x: vw * 0.85, y: vh * 0.52 };
    }
  }

  return { x: vw * 0.5, y: vh * 0.4 };
}

export function TableInteractionOverlay({
  tableThrows = [],
  voiceTaunts = {},
  game,
  isMn = true,
}: TableInteractionOverlayProps) {
  const [flights, setFlights] = useState<ActiveFlight[]>([]);
  const processedSeqRef = useRef<Set<number>>(new Set());
  const processedTauntsRef = useRef<Set<number>>(new Set());
  const lastRoomIdRef = useRef<string | null>(game?.roomId ?? null);

  // Clear flights and process caches when switching rooms
  useEffect(() => {
    if (game?.roomId && game.roomId !== lastRoomIdRef.current) {
      lastRoomIdRef.current = game.roomId;
      setFlights([]);
      processedSeqRef.current.clear();
      processedTauntsRef.current.clear();
      // Mark pre-existing throws as already seen so old animations never replay
      tableThrows.forEach((t) => processedSeqRef.current.add(t.seq));
      Object.values(voiceTaunts).forEach((t) => processedTauntsRef.current.add(t.seq));
    }
  }, [game?.roomId]);

  // Listen for new table throws
  useEffect(() => {
    if (!tableThrows || tableThrows.length === 0) return;

    const newThrows = tableThrows.filter((t) => {
      if (processedSeqRef.current.has(t.seq)) return false;
      // Skip stale throws older than 4.5s (in production seq is a millisecond timestamp > 1e12)
      if (t.seq > 1000000000000 && Date.now() - t.seq > 4500) {
        processedSeqRef.current.add(t.seq);
        return false;
      }
      return true;
    });

    if (newThrows.length === 0) return;

    newThrows.forEach((t) => {
      processedSeqRef.current.add(t.seq);

      const fromPos = getSeatPosition(t.fromSeat, game);
      const toPos = getSeatPosition(t.toSeat, game);

      const flight: ActiveFlight = {
        id: `flight-${t.seq}`,
        seq: t.seq,
        throwableId: t.throwableId,
        fromPlayerName: t.fromPlayerName || `${isMn ? 'Суудал' : 'Seat'} ${t.fromSeat + 1}`,
        toPlayerName: t.toPlayerName || `${isMn ? 'Суудал' : 'Seat'} ${t.toSeat + 1}`,
        fromSeat: t.fromSeat,
        toSeat: t.toSeat,
        fromX: fromPos.x,
        fromY: fromPos.y,
        toX: toPos.x,
        toY: toPos.y,
        toWidth: toPos.width,
        toHeight: toPos.height,
        daaluuTarget: toPos.daaluuTarget,
        startedAt: Date.now(),
        hasImpacted: false,
      };

      const FLIGHT_DURATION_MS = 1000;
      const IMPACT_DURATION_MS = 2200;
      const TOTAL_DURATION_MS = FLIGHT_DURATION_MS + IMPACT_DURATION_MS;

      setFlights((prev) => [...prev, flight]);

      // At 1000ms: projectile lands on target head with sound and impact graphics
      setTimeout(() => {
        playThrowableSound(t.throwableId);

        // Shake only an avatar; seat containers use transforms to hold their table position.
        if (typeof document !== 'undefined') {
          const targetEl =
            document.querySelector(`[data-seat="${t.toSeat}"] .seat-disc`) ||
            document.querySelector(`[data-seat="${t.toSeat}"] .player-avatar`) ||
            document.querySelector(`[data-seat="${t.toSeat}"] .avatar`);
          if (targetEl) {
            targetEl.classList.add('avatar-impact-shake');
            setTimeout(() => {
              targetEl.classList.remove('avatar-impact-shake');
            }, 1000);
          }
        }

        setFlights((prev) =>
          prev.map((f) => (f.id === flight.id ? { ...f, hasImpacted: true } : f)),
        );
      }, FLIGHT_DURATION_MS);

      // At 3400ms: remove flight and impact
      setTimeout(() => {
        setFlights((prev) => prev.filter((f) => f.id !== flight.id));
      }, TOTAL_DURATION_MS);
    });
  }, [tableThrows, game, isMn]);

  // Listen for voice taunts
  useEffect(() => {
    if (!voiceTaunts) return;

    Object.values(voiceTaunts).forEach((taunt) => {
      if (processedTauntsRef.current.has(taunt.seq)) return;
      // Skip stale taunts older than 4.5s
      if (taunt.seq > 1000000000000 && Date.now() - taunt.seq > 4500) {
        processedTauntsRef.current.add(taunt.seq);
        return;
      }
      processedTauntsRef.current.add(taunt.seq);
      playVoiceTaunt(taunt.tauntId, isMn ? 'mn' : 'en');
    });
  }, [voiceTaunts, isMn]);

  return (
    <div className="table-interaction-overlay-layer" aria-hidden="true">
      {/* 1. Target Lock Reticle (visible during flight towards target) */}
      {flights.map((f) => {
        if (f.hasImpacted) return null;
        return (
          <div
            key={`lock-${f.id}`}
            className="target-lock-reticle"
            style={{ left: `${f.toX}px`, top: `${f.toY}px` }}
          >
            <div className={`target-lock-ring${f.daaluuTarget ? ' is-daaluu-target' : ''}`}
              style={f.daaluuTarget ? { width: `${f.toWidth}px`, height: `${f.toHeight}px` } : undefined} />
            <div className="target-lock-label">🎯 {f.toPlayerName}</div>
          </div>
        );
      })}

      {/* 2. Visual Flight Trajectory Arcs (visible during flight) */}
      {flights.map((f) => {
        if (f.hasImpacted) return null;
        return (
          <FlightTrajectoryPath
            key={`path-${f.id}`}
            id={f.id}
            fromX={f.fromX}
            fromY={f.fromY}
            toX={f.toX}
            toY={f.toY}
          />
        );
      })}

      {/* 3. Top Impact Announcement Banner */}
      {flights
        .filter((f) => f.hasImpacted)
        .map((f) => (
          <div key={`banner-${f.id}`} className="impact-announcement-banner">
            <span className="banner-from">{f.fromPlayerName}</span>
            <span className="banner-action">{THROW_ACTION_VERBS[f.throwableId][isMn ? 'mn' : 'en']}</span>
            <span className="banner-to">{f.toPlayerName}</span>
          </div>
        ))}

      {/* 4. Active Flying Items or Head Impacts */}
      {flights.map((f) => {
        if (f.hasImpacted) {
          return (
            <div
              key={`impact-${f.id}`}
              className="impact-effect"
              style={{ left: `${f.toX}px`, top: `${f.toY}px` }}
            >
              <ImpactGraphic id={f.throwableId} isMn={isMn} />
            </div>
          );
        }

        return (
          <FlyingItem
            key={f.id}
            throwableId={f.throwableId}
            fromPlayerName={f.fromPlayerName}
            toPlayerName={f.toPlayerName}
            fromX={f.fromX}
            fromY={f.fromY}
            toX={f.toX}
            toY={f.toY}
          />
        );
      })}

      {/* 4. Floating Voice Taunt Speeches with Sender and Target tags */}
      {Object.values(voiceTaunts ?? {}).map((taunt) => {
        const script = VOICE_TAUNT_SCRIPTS[taunt.tauntId];
        if (!script) return null;
        const pos = getSeatPosition(taunt.fromSeat, game);
        const targetPlayer =
          taunt.targetSeat !== undefined
            ? game?.players.find((p) => p.seat === taunt.targetSeat)
            : null;

        return (
          <div
            key={`taunt-${taunt.seq}`}
            className="table-voice-bubble"
            style={{ left: `${pos.x}px`, top: `${pos.y - 45}px` }}
          >
            <div className="voice-bubble-header">
              <span className="voice-bubble-speaker">{script.icon}</span>
              <span className="voice-bubble-route">
                {taunt.fromPlayerName} {targetPlayer ? `➔ ${targetPlayer.name}` : isMn ? '➔ Бүгдэд' : '➔ Everyone'}
              </span>
            </div>
            <div className="voice-bubble-body">
              <span className="voice-bubble-text">"{script[isMn ? 'mn' : 'en']}"</span>
              <span className="voice-audio-waves" aria-hidden="true">
                <span className="audio-bar ab-1" />
                <span className="audio-bar ab-2" />
                <span className="audio-bar ab-3" />
                <span className="audio-bar ab-4" />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Visual flight trajectory path arched across the table felt
 */
function FlightTrajectoryPath({
  id,
  fromX,
  fromY,
  toX,
  toY,
}: {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}) {
  const distance = Math.hypot(toX - fromX, toY - fromY);
  const arcHeight = Math.max(80, Math.min(210, distance * 0.32));
  const midX = (fromX + toX) / 2;
  const controlY = (fromY + toY) / 2 - arcHeight * 2;
  const pathD = `M ${fromX} ${fromY} Q ${midX} ${controlY} ${toX} ${toY}`;

  return (
    <svg className="flight-trajectory-svg" aria-hidden="true">
      <defs>
        <linearGradient id={`traj-grad-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.2" />
          <stop offset="35%" stopColor="#fde047" stopOpacity="0.85" />
          <stop offset="85%" stopColor="#fbbf24" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#ef4444" stopOpacity="0.85" />
        </linearGradient>
        <filter id={`traj-glow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="0" stdDeviation="3.5" floodColor="#f59e0b" floodOpacity="0.65" />
        </filter>
      </defs>
      {/* Outer ambient glow halo along the trajectory */}
      <path
        d={pathD}
        fill="none"
        stroke="rgba(245, 158, 11, 0.22)"
        strokeWidth="7"
        strokeLinecap="round"
      />
      {/* Animated dotted trajectory arc line */}
      <path
        d={pathD}
        fill="none"
        stroke={`url(#traj-grad-${id})`}
        strokeWidth="2.8"
        strokeDasharray="7 8"
        strokeLinecap="round"
        className="flight-trajectory-dashed"
        filter={`url(#traj-glow-${id})`}
      />
    </svg>
  );
}

/**
 * Animated flying item with high-visibility flight tracer tag (From ➔ To)
 * and trailing motion stardust particles
 */
function FlyingItem({
  throwableId,
  fromPlayerName,
  toPlayerName,
  fromX,
  fromY,
  toX,
  toY,
}: {
  throwableId: ThrowableId;
  fromPlayerName: string;
  toPlayerName: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tracerRef = useRef<HTMLDivElement>(null);
  const trail1Ref = useRef<HTMLDivElement>(null);
  const trail2Ref = useRef<HTMLDivElement>(null);
  const trail3Ref = useRef<HTMLDivElement>(null);
  const trail4Ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    const tracerEl = tracerRef.current;
    const trailNodes = [trail1Ref.current, trail2Ref.current, trail3Ref.current, trail4Ref.current];
    if (!el) return;

    const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const duration = 1000; // Slower, clearly visible physical flight (1.0s)
    const distance = Math.hypot(toX - fromX, toY - fromY);
    const arcHeight = Math.max(80, Math.min(210, distance * 0.32));
    const isThrowRight = toX >= fromX;
    const spinDirection = isThrowRight ? 1 : -1;
    const totalRotation = spinDirection * 240; // Natural single-toss tumble

    const history: Array<{ x: number; y: number }> = [];
    let rafId: number;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, Math.max(0, elapsed / duration));

      // Ease-out with gentle launch: readable across the entire table
      const ease = 1 - Math.pow(1 - progress, 1.45);

      const currentX = fromX + (toX - fromX) * ease;
      const arcOffset = 4 * arcHeight * progress * (1 - progress);
      const currentY = fromY + (toY - fromY) * ease - arcOffset;

      const currentRotation = progress * totalRotation;
      const currentScale = 1.0 + Math.sin(progress * Math.PI) * 0.42;

      el.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) translate(-50%, -50%) rotate(${currentRotation}deg) scale(${currentScale})`;
      if (tracerEl) {
        tracerEl.style.transform = `rotate(${-currentRotation}deg)`;
      }

      // Record trajectory history for trailing particles
      history.unshift({ x: currentX, y: currentY });
      if (history.length > 25) history.pop();

      const offsets = [3, 6, 9, 12];
      for (let i = 0; i < trailNodes.length; i++) {
        const node = trailNodes[i];
        const pt = history[offsets[i]];
        if (node && pt) {
          const pScale = Math.max(0.2, (0.8 - i * 0.15) * currentScale);
          const pOpacity = Math.max(0, (0.75 - i * 0.16) * (1 - progress * 0.15));
          node.style.transform = `translate3d(${pt.x}px, ${pt.y}px, 0) translate(-50%, -50%) scale(${pScale})`;
          node.style.opacity = `${pOpacity}`;
        }
      }

      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [fromX, fromY, toX, toY]);

  return (
    <>
      <div
        ref={trail4Ref}
        className="flying-trail-particle flying-trail-p4"
        style={{ left: 0, top: 0, opacity: 0 }}
      />
      <div
        ref={trail3Ref}
        className="flying-trail-particle flying-trail-p3"
        style={{ left: 0, top: 0, opacity: 0 }}
      />
      <div
        ref={trail2Ref}
        className="flying-trail-particle flying-trail-p2"
        style={{ left: 0, top: 0, opacity: 0 }}
      />
      <div
        ref={trail1Ref}
        className="flying-trail-particle flying-trail-p1"
        style={{ left: 0, top: 0, opacity: 0 }}
      />
      <div
        ref={containerRef}
        className="flying-throwable"
        style={{
          left: 0,
          top: 0,
          transform: `translate3d(${fromX}px, ${fromY}px, 0) translate(-50%, -50%) scale(1)`,
          willChange: 'transform',
        }}
      >
        <ProjectileGraphic id={throwableId} />
        <div
          ref={tracerRef}
          className="flight-tracer-tag"
        >
          <span className="tracer-from">{fromPlayerName}</span>
          <span className="tracer-arrow">➔</span>
          <span className="tracer-to">{toPlayerName}</span>
        </div>
      </div>
    </>
  );
}
